// 📄 components/Configuracion/ApiDefinitionForm.tsx
// Campos del formulario de una API del proyecto — extraído de
// ApiDefinitionCard.tsx para reutilizarlo tanto en la fila expandible de
// la lista (ApiDefinitionCard.tsx) como en NuevaApiModal.tsx (alta de una
// API nueva). Mismos campos, misma lógica que antes — esto es solo una
// extracción, no un cambio de comportamiento.
import { useState } from "react";
import { Lock, Plus, KeyRound, ClipboardPaste } from "lucide-react";
import { ApiDefinition, ApiEndpointConfig } from "../../services/clienteConexionService.ts";
import { ApiTemplate } from "../../services/catalogoApisService.ts";
import { TIPOS_API, PROTOCOLOS_API, AMBIENTES_API } from "./apiTiposConfig.ts";
import { ARIBA_DATA_CENTERS, normalizarAribaDataCenter } from "./aribaDataCenters.ts";
import EndpointConfigCard from "./EndpointConfigCard.tsx";
import EndpointCollapsibleItem from "./EndpointCollapsibleItem.tsx";
import ImportarCurlModal from "../shared/ImportarCurlModal.tsx";
import { ParsedCurlResult } from "../../utils/curlParser.ts";

interface ApiDefinitionFormProps {
  api: ApiDefinition;
  editable: boolean;
  // Plantillas del Catálogo de APIs Estándar (Admin) — ver comentario en
  // ApiDefinitionCard.tsx sobre por qué son solo sugerencia, nunca
  // restricción.
  catalogo: ApiTemplate[];
  onChange: (api: ApiDefinition) => void;
}

const generarId = (): string => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const nuevoEndpoint = (): ApiEndpointConfig => ({
  id: generarId(),
  nombre: "",
  endpoint: "",
  metodo: "GET",
  bodyEjemplo: "",
  params: [],
  headers: [],
  pathVariables: []
});

export default function ApiDefinitionForm({ api, editable, catalogo, onChange }: ApiDefinitionFormProps) {
  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1 flex items-center gap-1.5";
  const tipoMeta = TIPOS_API[api.tipo] || TIPOS_API.ariba;
  // authTipo es elegible por API (ver radio button más abajo) — antes se
  // derivaba solo del `tipo` (Ariba = siempre OAuth2), pero "Flow
  // Extension API" es Ariba y NO usa OAuth (pide Basic Auth manual +
  // headers fijos, sin token). `api.authTipo` puede venir vacío en APIs
  // guardadas antes de que este campo existiera — se cae al default de
  // su tipo, mismo criterio de siempre.
  const authTipoEfectivo = api.authTipo || tipoMeta.authTipo;
  const esOauth = authTipoEfectivo === "oauth";
  const [plantillaId, setPlantillaId] = useState("");
  const [mostrarImportarCurl, setMostrarImportarCurl] = useState(false);

  // Qué endpoints están expandidos — solo UI, no es parte del modelo de
  // datos. Por defecto todos colapsados (ver reglas en el render de
  // abajo: 1 solo endpoint siempre expandido, uno recién agregado o
  // importado por cURL también).
  const [endpointsExpandidos, setEndpointsExpandidos] = useState<Record<string, boolean>>({});
  const toggleEndpointExpandido = (id: string) => setEndpointsExpandidos((prev) => ({ ...prev, [id]: !prev[id] }));

  const update = (field: keyof ApiDefinition, value: string) => {
    onChange({ ...api, [field]: value });
  };

  // urlBase/tokenUrl sugeridos por Data Center, leídos del catálogo del
  // Admin (Ariba) en vez de un valor hardcodeado acá — primera plantilla
  // del catálogo que declare ese Data Center con algo cargado.
  const sugerenciaPorDataCenter: Record<string, { urlBase: string; tokenUrl: string }> = {};
  for (const t of catalogo) {
    if (t.tipo !== "ariba" || !t.dataCenter || sugerenciaPorDataCenter[t.dataCenter]) continue;
    if (t.urlBase || t.tokenUrl) sugerenciaPorDataCenter[t.dataCenter] = { urlBase: t.urlBase, tokenUrl: t.tokenUrl };
  }
  // Data Center resuelve el host de ejecución (ver resolverDataCenter en
  // functions/providers/ariba.js) para CUALQUIER API Ariba, sea OAuth2 o
  // Basic — Flow Extension API también corre contra un host por Data
  // Center, solo cambia cómo se autentica, no dónde vive.
  const dataCenterActual = api.tipo === "ariba" ? normalizarAribaDataCenter(api.dataCenter) : "";
  const sugeridoActual = dataCenterActual ? sugerenciaPorDataCenter[dataCenterActual] : undefined;
  const urlBasePlaceholder = sugeridoActual?.urlBase || (api.tipo === "s4hana" ? "https://myxxxxxx-api.s4hana.cloud.sap" : "https://eu.openapi.ariba.com");
  const tokenUrlPlaceholder = sugeridoActual?.tokenUrl || "https://api.ariba.com/v2/oauth/token";

  const catalogoDelTipo = catalogo.filter((t) => t.tipo === api.tipo);

  const handleSeleccionarDataCenter = (codigo: string) => {
    if (!codigo) {
      onChange({ ...api, dataCenter: "" });
      return;
    }
    // Solo sugiere (cortesía) si el campo está vacío — nunca pisa una
    // URL que el consultor ya haya escrito a mano, sin importar qué Data
    // Center elija después. Mientras el campo siga vacío, la sugerencia
    // se sigue viendo como placeholder (ver urlBasePlaceholder arriba).
    const sugerido = sugerenciaPorDataCenter[codigo];
    // `tokenUrl` (y otros campos solo-OAuth) puede venir undefined en una
    // API guardada en modo Basic (nunca se persistió, ver configService.js
    // → CAMPOS_POR_AUTH_TIPO) — Data Center en cambio aplica a Ariba
    // entero sin importar authTipo, así que este handler se puede disparar
    // igual desde una API en modo Basic. `|| ""` evita el TypeError.
    onChange({
      ...api,
      dataCenter: codigo,
      urlBase: (api.urlBase || "").trim() ? api.urlBase : (sugerido?.urlBase || ""),
      tokenUrl: (api.tokenUrl || "").trim() ? api.tokenUrl : (sugerido?.tokenUrl || "")
    });
  };

  // Prellenado rápido desde una plantilla completa del catálogo: mismo
  // criterio "solo si está vacío" campo por campo — nunca sobrescribe lo
  // que ya se escribió. Los endpoints solo se copian si esta API todavía
  // no tiene ninguno (evita duplicarlos si se aplica más de una vez), y
  // se les regenera el id para no colisionar con otra API que use la
  // misma plantilla.
  const handleAplicarPlantilla = (id: string) => {
    const tpl = catalogoDelTipo.find((t) => t.id === id);
    setPlantillaId("");
    if (!tpl) return;
    onChange({
      ...api,
      urlBase: (api.urlBase || "").trim() ? api.urlBase : tpl.urlBase,
      api: (api.api || "").trim() ? api.api : tpl.api,
      protocolo: api.protocolo || tpl.protocolo,
      dataCenter: api.dataCenter || tpl.dataCenter,
      // A diferencia de los campos de arriba (solo se completan si están
      // vacíos), authTipo se aplica siempre que se elige una plantilla —
      // es la plantilla la que sabe si esta API necesita OAuth2 o no (ver
      // Flow Extension API), y en el 99% de los casos se aplica recién
      // creada la API, cuando authTipo todavía es el default genérico del
      // tipo, no una elección deliberada del usuario.
      authTipo: tpl.authTipo || api.authTipo,
      tokenUrl: (api.tokenUrl || "").trim() ? api.tokenUrl : tpl.tokenUrl,
      grantType: api.grantType || tpl.grantType,
      endpoints: api.endpoints.length > 0 ? api.endpoints : tpl.endpoints.map((ep) => ({ ...ep, id: generarId() }))
    });
  };

  // Rellena esta API con lo que el parser de cURL logró inferir (ver
  // components/shared/ImportarCurlModal.tsx / utils/curlParser.ts), con el
  // mismo criterio "cortesía, solo si está vacío" que handleAplicarPlantilla.
  // A diferencia del catálogo, acá SÍ llegan credenciales (API Key,
  // Client ID/OAuth Secret o Usuario/Contraseña) porque son propias de este
  // proyecto — el modal ya resolvió el conflicto con credenciales
  // existentes antes de llamar a onImportar, así que acá solo se aplican.
  const handleImportarCurl = (resultado: ParsedCurlResult) => {
    const creds = resultado.credencialesImportadas;
    // Todos los campos "solo OAuth" (tokenUrl/clientId/oauthSecret) o
    // "solo Basic" (usuario/contraseña) pueden venir undefined según el
    // authTipo con el que se guardó esta API la última vez (ver
    // configService.js) — `|| ""` evita un TypeError al importar sobre
    // una API que nunca tuvo ese campo.
    const actualizada: ApiDefinition = {
      ...api,
      urlBase: (api.urlBase || "").trim() ? api.urlBase : (resultado.urlBase ?? api.urlBase),
      api: (api.api || "").trim() ? api.api : (resultado.api ?? api.api),
      dataCenter: api.dataCenter || (resultado.dataCenter ?? api.dataCenter),
      tokenUrl: (api.tokenUrl || "").trim() ? api.tokenUrl : (resultado.tokenUrl ?? api.tokenUrl),
      grantType: api.grantType || (resultado.grantType ?? api.grantType),
      apiKey: (api.apiKey || "").trim() ? api.apiKey : (creds?.apiKey ?? api.apiKey),
      clientId: (api.clientId || "").trim() ? api.clientId : (creds?.clientId ?? api.clientId),
      oauthSecret: (api.oauthSecret || "").trim() ? api.oauthSecret : (creds?.oauthSecret ?? api.oauthSecret),
      usuario: (api.usuario || "").trim() ? api.usuario : (creds?.usuario ?? api.usuario),
      contraseña: (api.contraseña || "").trim() ? api.contraseña : (creds?.contraseña ?? api.contraseña)
    };

    if (resultado.metodo && resultado.api) {
      const epImportado: ApiEndpointConfig = {
        ...nuevoEndpoint(),
        endpoint: resultado.endpointSugerido || "",
        metodo: resultado.metodo,
        bodyEjemplo: resultado.bodyEjemplo || "",
        params: resultado.params || [],
        headers: resultado.headers || [],
        pathVariables: resultado.pathVariablesSugeridas || []
      };
      actualizada.endpoints = [...actualizada.endpoints, epImportado];
      setEndpointsExpandidos((prev) => ({ ...prev, [epImportado.id]: true }));
    }

    onChange(actualizada);
    setMostrarImportarCurl(false);
  };

  const addEndpoint = () => {
    const ep = nuevoEndpoint();
    onChange({ ...api, endpoints: [...api.endpoints, ep] });
    setEndpointsExpandidos((prev) => ({ ...prev, [ep.id]: true }));
  };

  const updateEndpoint = (id: string, actualizado: ApiEndpointConfig) => {
    onChange({ ...api, endpoints: api.endpoints.map((ep) => (ep.id === id ? actualizado : ep)) });
  };

  const removeEndpoint = (id: string) => {
    onChange({ ...api, endpoints: api.endpoints.filter((ep) => ep.id !== id) });
    setEndpointsExpandidos((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center justify-between gap-3 mb-1">
          <label className="text-xs font-medium text-gray-400">Nombre de la API</label>
          {editable && (
            <button
              type="button"
              onClick={() => setMostrarImportarCurl(true)}
              className="flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
            >
              <ClipboardPaste className="w-3.5 h-3.5" /> Importar desde cURL
            </button>
          )}
        </div>
        <input
          type="text"
          className={inputClass}
          value={api.nombre}
          onChange={(e) => update("nombre", e.target.value)}
          placeholder={api.tipo === "s4hana" ? "Ej: S/4HANA Attachment Content" : "Ej: Ariba Supplier Data API"}
          disabled={!editable}
        />
      </div>

      {mostrarImportarCurl && (
        <ImportarCurlModal
          modo="proyecto"
          esAriba={esOauth}
          authTipoEsperado={authTipoEfectivo}
          tieneCredencialesGuardadas={api.tieneCredencialesGuardadas}
          onClose={() => setMostrarImportarCurl(false)}
          onImportar={handleImportarCurl}
        />
      )}

      {editable && catalogoDelTipo.length > 0 && (
        <div>
          <label className={labelClass}>Prellenar desde plantilla del catálogo (opcional)</label>
          <select
            className={inputClass}
            value={plantillaId}
            onChange={(e) => handleAplicarPlantilla(e.target.value)}
          >
            <option value="">Elegir plantilla del catálogo Admin...</option>
            {catalogoDelTipo.map((t) => (
              <option key={t.id} value={t.id}>{t.nombre || "Plantilla sin nombre"}</option>
            ))}
          </select>
          <p className="text-[11px] text-gray-500 mt-1">Aplica los valores de la plantilla solo en los campos que todavía estén vacíos — nunca sobrescribe lo que ya escribiste.</p>
        </div>
      )}

      <div className="bg-black/20 border border-white/10 rounded-xl p-4 space-y-4">
        <h4 className="text-xs font-semibold text-gray-300 flex items-center gap-2 uppercase tracking-wider">
          <KeyRound className="w-3.5 h-3.5 text-blue-400" /> Credenciales de esta API ({tipoMeta.label})
        </h4>

        {editable && api.tipo === "ariba" && (
          <div className="flex flex-wrap items-center gap-4 -mt-1">
            <label className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer">
              <input
                type="radio"
                checked={esOauth}
                onChange={() => onChange({ ...api, authTipo: "oauth" })}
              />
              OAuth2 (pide token automático)
            </label>
            <label className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer">
              <input
                type="radio"
                checked={!esOauth}
                onChange={() => onChange({ ...api, authTipo: "basic" })}
              />
              Manual / Básica (sin token — usuario/contraseña o API Key fijos)
            </label>
          </div>
        )}

        {api.tieneCredencialesGuardadas && (
          <p className="text-xs text-gray-400 bg-black/20 border border-white/10 rounded-lg p-3">
            {esOauth
              ? "Ya hay un API Key y OAuth Secret guardados para esta API. Completa los campos de abajo solo si quieres reemplazarlos."
              : "Ya hay una contraseña guardada para esta API. Complétala de nuevo solo si quieres reemplazarla."}
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {api.tipo === "ariba" && (
            <div>
              <label className={labelClass}><Lock className="w-3 h-3 text-amber-400" /> API Key <span className="text-amber-400/70 normal-case font-normal">(sensible)</span></label>
              <input type="password" className={inputClass} value={api.apiKey} onChange={(e) => update("apiKey", e.target.value)} placeholder="••••••••" disabled={!editable} />
            </div>
          )}
          {esOauth ? (
            <>
              <div>
                <label className={labelClass}><Lock className="w-3 h-3 text-amber-400" /> OAuth Secret <span className="text-amber-400/70 normal-case font-normal">(sensible)</span></label>
                <input type="password" className={inputClass} value={api.oauthSecret} onChange={(e) => update("oauthSecret", e.target.value)} placeholder="••••••••" disabled={!editable} />
              </div>
              <div>
                <label className={labelClass}>Client ID</label>
                <input type="text" className={inputClass} value={api.clientId} onChange={(e) => update("clientId", e.target.value)} placeholder="Client ID" disabled={!editable} />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className={labelClass}>Usuario</label>
                <input type="text" className={inputClass} value={api.usuario} onChange={(e) => update("usuario", e.target.value)} placeholder="Usuario" disabled={!editable} />
              </div>
              <div>
                <label className={labelClass}><Lock className="w-3 h-3 text-amber-400" /> Contraseña <span className="text-amber-400/70 normal-case font-normal">(sensible)</span></label>
                <input type="password" className={inputClass} value={api.contraseña} onChange={(e) => update("contraseña", e.target.value)} placeholder="••••••••" disabled={!editable} />
              </div>
            </>
          )}
          {api.tipo === "ariba" && (
            <div>
              <label className={labelClass}>Data Center</label>
              <select
                className={inputClass}
                value={normalizarAribaDataCenter(api.dataCenter)}
                onChange={(e) => handleSeleccionarDataCenter(e.target.value)}
                disabled={!editable}
              >
                <option value="">Selecciona un Data Center</option>
                {ARIBA_DATA_CENTERS.map((dc) => (
                  <option key={dc.codigo} value={dc.codigo} disabled={!dc.disponible}>
                    {dc.label}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-gray-500 mt-1">Valor sugerido por el catálogo Admin según el Data Center seleccionado. Puedes editarlo libremente si tu proyecto usa una URL personalizada.</p>
            </div>
          )}
          {esOauth && (
            <>
              <div>
                <label className={labelClass}>Realm</label>
                <input type="text" className={inputClass} value={api.realm} onChange={(e) => update("realm", e.target.value)} placeholder="Realm" disabled={!editable} />
              </div>
              <div>
                <label className={labelClass}>ANID</label>
                <input type="text" className={inputClass} value={api.anid} onChange={(e) => update("anid", e.target.value)} placeholder="ANID" disabled={!editable} />
              </div>
            </>
          )}
          <div>
            <label className={labelClass}>URL Base</label>
            <input
              type="text"
              className={inputClass}
              value={api.urlBase}
              onChange={(e) => update("urlBase", e.target.value)}
              placeholder={urlBasePlaceholder}
              disabled={!editable}
            />
            {esOauth && !!dataCenterActual && (
              <p className="text-[11px] text-gray-500 mt-1">Editable siempre — el Data Center solo sugiere un valor inicial.</p>
            )}
          </div>
          <div>
            <label className={labelClass}>API</label>
            <input
              type="text"
              className={inputClass}
              value={api.api}
              onChange={(e) => update("api", e.target.value)}
              placeholder={api.tipo === "s4hana" ? "/sap/opu/odata/sap/API_CV_ATTACHMENT_SRV" : "/api/supplierdatapagination/v4/prod"}
              disabled={!editable}
            />
          </div>
          <div>
            <label className={labelClass}>Protocolo</label>
            <select className={inputClass} value={api.protocolo} onChange={(e) => update("protocolo", e.target.value)} disabled={!editable}>
              <option value="">Sin especificar</option>
              {PROTOCOLOS_API.map((protocolo) => (
                <option key={protocolo} value={protocolo}>{protocolo}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Ambiente</label>
            <select className={inputClass} value={api.ambiente} onChange={(e) => update("ambiente", e.target.value)} disabled={!editable}>
              <option value="">Sin especificar</option>
              {AMBIENTES_API.map((ambiente) => (
                <option key={ambiente} value={ambiente}>{ambiente}</option>
              ))}
            </select>
          </div>
        </div>
        <p className="text-[11px] text-gray-500">URL Base y API se piden una sola vez para esta API — todos sus endpoints las comparten.</p>
      </div>

      {esOauth && (
        <div className="bg-black/20 border border-white/10 rounded-xl p-4 space-y-3">
          <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wider">Token</h4>
          <div>
            <label className={labelClass}>URL del Token</label>
            <input
              type="text"
              className={inputClass}
              value={api.tokenUrl}
              onChange={(e) => update("tokenUrl", e.target.value)}
              placeholder={tokenUrlPlaceholder}
              disabled={!editable}
            />
            {!!dataCenterActual && (
              <p className="text-[11px] text-gray-500 mt-1">Valor sugerido por el catálogo Admin según el Data Center seleccionado. Puedes editarlo libremente si tu proyecto usa una URL personalizada.</p>
            )}
          </div>
          <div>
            <label className={labelClass}>Grant Type</label>
            <select
              className={inputClass}
              value={api.grantType || ""}
              onChange={(e) => update("grantType", e.target.value)}
              disabled={!editable}
            >
              <option value="">Automático (openapi_2lo)</option>
              <option value="openapi_2lo">openapi_2lo</option>
              <option value="client_credentials">client_credentials</option>
            </select>
            <p className="text-[11px] text-gray-500 mt-1">La mayoría de OpenAPIs de Ariba usan openapi_2lo; algunas más nuevas piden client_credentials.</p>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wider">Endpoints</h4>
          {editable && (
            <button
              type="button"
              onClick={addEndpoint}
              className="flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Agregar endpoint
            </button>
          )}
        </div>

        {api.endpoints.length === 0 ? (
          <p className="text-xs text-gray-500 bg-black/20 border border-white/10 rounded-lg p-3">
            Todavía no hay endpoints configurados para esta API.
          </p>
        ) : api.endpoints.length === 1 ? (
          // Un solo endpoint: no vale la pena colapsarlo, se muestra
          // siempre expandido tal como antes.
          <EndpointConfigCard
            endpoint={api.endpoints[0]}
            urlBase={api.urlBase}
            api={api.api}
            readOnly={!editable}
            onChange={(actualizado) => updateEndpoint(api.endpoints[0].id, actualizado)}
            onRemove={() => removeEndpoint(api.endpoints[0].id)}
          />
        ) : (
          <div className="space-y-3">
            {api.endpoints.map((ep) => (
              <EndpointCollapsibleItem
                key={ep.id}
                endpoint={ep}
                urlBase={api.urlBase}
                api={api.api}
                readOnly={!editable}
                expandido={!!endpointsExpandidos[ep.id]}
                onToggle={() => toggleEndpointExpandido(ep.id)}
                onChange={(actualizado) => updateEndpoint(ep.id, actualizado)}
                onRemove={() => removeEndpoint(ep.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
